import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactApexChart from 'react-apexcharts';
import { clientRevenueOverviewPerClient } from './Services/reportRevenue';
import typeOfContract from '../../../../constants/typeOfContract';

function RevenueOverviewPerClient({ trackReport, trackReportLoading, trackReportError, projectCost, clientInvoices, clientInvoicesLoading, clientInvoicesError, expenses }) {
  const reportingRevenue = useSelector((state) => state.reportingRevenue);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const setReportingCurrency = useSelector((state) => state.reportingRevenue.revenueCurrency);
  var currencies = "USD";
  if (setReportingCurrency && setReportingCurrency.length === 1) {
    currencies = setReportingCurrency[0];
  } else {
    currencies = settings?.currency || "USD";
  }

  // All hooks must be called unconditionally before any early returns
  const processedData = useMemo(() => {
    if (!trackReport || !projectCost || !clientInvoices || !expenses || !reportingRevenue || !forex) {
      return [];
    }
    try {
      return clientRevenueOverviewPerClient(
        trackReport,
        projectCost,
        clientInvoices,
        reportingRevenue,
        currencies,
        forex,
        typeOfContract,
        expenses
      ) || [];
    } catch (error) {
      console.error('Error processing revenue per client data:', error);
      return [];
    }
  }, [trackReport, projectCost, clientInvoices, reportingRevenue, currencies, forex, typeOfContract, expenses]);

  const series = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    return processedData.map(client => parseFloat(client.revenue || 0));
  }, [processedData]);

  const labels = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    return processedData.map(client => client.name || '');
  }, [processedData]);

  // Colors array for all clients
  const allColors = [
    '#8AA9D2', '#234266', '#6D98CF', '#4263A5',
    '#6BA9E2', '#9ACBFB', '#75AECF', '#3f4b57',
    '#6A9DCB', '#A4C4E6', '#9AC9F3', '#000',
    '#89A7C4', '#8EA9C6', '#BBCFE7', '#B7D4F0',
    '#A6C0D6', '#4B6F98', '#A2B5CB', '#86A0BC'
  ];

  // Function to get top 3 series and corresponding labels and colors
  const getTop3Series = useMemo(() => {
    if (!series || series.length === 0 || !labels || labels.length === 0) {
      return { top3Labels: [], top3Colors: [] };
    }
    const seriesWithIndices = series.map((value, index) => ({ value, index }));
    seriesWithIndices.sort((a, b) => b.value - a.value);
    const top3Series = seriesWithIndices.slice(0, 3);
    const top3Labels = top3Series.map(item => labels[item.index]);
    const top3Colors = top3Series.map(item => allColors[item.index % allColors.length]);
    return { top3Labels, top3Colors };
  }, [series, labels]);

  const { top3Labels, top3Colors } = getTop3Series;

  const hasNoData = series.length === 0 || series.every(value => value <= 0);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    if (hasNoData) {
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setShowMessage(false);
    }
  }, [hasNoData]);

  // Early returns after all hooks
  if (trackReportLoading || clientInvoicesLoading) {
    return (
      <div className="outer-piechart-report">
        <div className="outer-piechart-title outer-piechart-title-desktop">
          Revenue Overview per Client
        </div>
        <div className="outer-piechart-section">
          <div className="outer-piechart-title outer-piechart-title-mobile">
            Revenue Overview per Client
          </div>
          {/* Donut chart skeleton */}
          <div style={{
            width: '200px',
            height: '235px',
            margin: '0 auto',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Outer donut ring skeleton with colored segments */}
            <div style={{
              width: '193.845px',
              height: '193.845px',
              borderRadius: '50%',
              border: '30px solid',
              borderTopColor: '#8AA9D2',
              borderRightColor: '#234266',
              borderBottomColor: '#6D98CF',
              borderLeftColor: '#4263A5',
              opacity: 0.3,
              animation: 'pulse 1.5s ease-in-out infinite',
              position: 'relative',
              transform: 'rotate(-45deg)',
              boxSizing: 'border-box'
            }}>
              {/* Inner circle (donut hole) */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '133.845px',
                height: '133.845px',
                borderRadius: '50%',
                backgroundColor: '#ffffff',
              }} />
            </div>
          </div>
        </div>
        {/* Legend skeleton */}
        <div className="custom-legend">
          <div className="custom-legend-title">Top 3 Clients</div>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="custom-legend-item">
              <div className="custom-legend-value">
                <span
                  className="custom-legend-marker"
                  style={{
                    backgroundColor: '#e5e7eb',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${index * 0.1}s`
                  }}
                ></span>
                <div style={{
                  width: '120px',
                  height: '14px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${index * 0.1}s`
                }} />
              </div>
              <div className="custom-llegend-value">
                <div style={{
                  width: '60px',
                  height: '14px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${index * 0.1}s`
                }} />
              </div>
            </div>
          ))}
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (trackReportError || clientInvoicesError) return <div>Error loading data.</div>;

  const options = {
    chart: {
      width: 193.845,
      height: 193.845,
      type: 'donut',
    },
    plotOptions: {
      pie: {
        startAngle: -45,
        endAngle: 315,
        expandOnClick: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'gradient',
    },
    legend: {
      show: false,
    },
    labels: labels,
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return val + '%';
        },
      },
      custom: function ({ series, seriesIndex, w }) {
        const client = labels[seriesIndex];
        const hoursLogged = series[seriesIndex];
        const totalHours = series.reduce((total, val) => total + val, 0);
        const percentage = totalHours > 0 ? ((hoursLogged / totalHours) * 100).toFixed(2) : '0.00';
        return `<div class="apexcharts-tooltip-custom" style="background: #FFFFFF; box-shadow: 0px 1px 8px rgba(110, 110, 110, 0.1); border-radius: 0px;height: 44pxxpx;padding: 5px 10px;">
                  <span class="apexcharts-tooltip-marker"></span>
                  <span class="apexcharts-tooltip-text" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${client}</span>
                  <span class="apexcharts-tooltip-value" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${new Intl.NumberFormat("en-US", {style: "currency", currency: currencies || 'USD',}).format(hoursLogged)} - ${percentage}%</span>
                </div>`;
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
    colors: allColors,
    states: {
      active: {
        filter: {
          type: "none",
        },
      },
      normal: {
        filter: {
          type: "none",
        },
      },
    },
  };

  // Custom Legend Component
  const CustomLegend = () => {
    return (
      <div className='custom-legend'>
        <div className='custom-legend-title'>Top 3 Clients</div>
        {top3Labels.map((label, index) => {
          const labelIndex = labels.indexOf(label);
          const value = labelIndex >= 0 && labelIndex < series.length ? series[labelIndex] : 0;
          return (
            <div key={label} className='custom-legend-item'>
              <div className='custom-legend-value'>
                <span className='custom-legend-marker' style={{ backgroundColor: top3Colors[index] }}></span>
                <span className='custom-legend-text'>{label}</span>
              </div>
              <div className='custom-llegend-value'>{new Intl.NumberFormat("en-US", {style: "currency", currency: currencies || 'USD',}).format(value)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className='outer-piechart-report'>
      <div className='outer-piechart-title outer-piechart-title-desktop'>
        Revenue Overview per Client
      </div>
      <div className='outer-piechart-section' style={{ display: 'flex', flexDirection: 'column', minHeight: '235px' }}>
        <div className='outer-piechart-title outer-piechart-title-mobile'>
          Revenue Overview per Client
        </div>
        {hasNoData && showMessage ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '235px' }}>
            <p className='no-data-text' style={{ margin: 0 }}>No data to display</p>
          </div>
        ) : (
          <ReactApexChart
            options={options}
            series={series}
            type='donut'
            width={200}
            height={235}
          />
        )}
      </div>
      {!hasNoData && top3Labels.length > 0 && <CustomLegend />}
    </div>
  );
}

export default RevenueOverviewPerClient;
